import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";

import Badge from "../ui/badge/Badge";
import { Trash2, AlertCircle } from 'lucide-react';
import MoreButton from '../common/MoreButton';
import { useNavigate, useParams } from "react-router";
import api from "../../services/api";
import authService from "../../services/authService";
import Pagination from "../ui/pagination/Pagination";
import ComponentCard from "../common/ComponentCard";
import Button from "../ui/button/Button";
import Modal from '../common/Modal';
import Alert from '../ui/alert/Alert';

// Definição da interface da oferta
interface Coproducer {
    id: string;
    coproducerId?: string;
    companyId?: string;
    mainCompanyId?: string;
    email: string;
    company?: {
        id: string;
        name: string;
    };
    product?: {
        id: string;
        name: string;
    };
    contractEnd?: string;
    contractStart?: string;
    contractDuration?: number;
    expiresAt?: string;
    sentAt?: string;
    acceptedAt?: string;
    percentage: number | string;
    productId: string;
    participantInvitationId?: string;
    type: string;
    createdAt: string;
    updatedAt: string;
    status: string;
    token?: string;
}

interface ProductOfferTableProps {
    productId?: string;
}

export default function CoProducerTable({ productId }: ProductOfferTableProps) {
    const navigate = useNavigate();
    const { id: paramId } = useParams<{ id: string }>();
    const productIdToUse = productId || paramId;
    const companyId = authService.getCompanyId();

    // Dados fixos para a tabela (posteriormente serão substituídos por dados da API)
    const [coproducers, setCoproducers] = useState<Coproducer[]>([]);
    const [sentInvitations, setSentInvitations] = useState<Coproducer[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: "success" | "error" | "warning" | "info";
        message: string;
        title: string;
    } | null>(null);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Estados para o modal de exclusão
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [coproducerToDelete, setCoproducerToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchProductCoproducers = async () => {
            try {
                setLoading(true);
                setError(null);

                // Buscando os co-produtores do produto
                const response = await api.get(`/api/product-participants/product/${productIdToUse}/coproducers`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.success) {
                    setCoproducers(response.data.data);
                } else {
                    setCoproducers([]);
                }
            } catch (err: any) {
                console.error("Erro ao buscar co-produtores do produto:", err);
                setError(err.message || "Erro ao carregar co-produtores do produto");
            } finally {
                setLoading(false);
            }
        };

        if (productIdToUse) {
            fetchProductCoproducers();
        }
    }, [productIdToUse]);

    useEffect(() => {
        const fetchPendingInvitation = async () => {
            try {
                setLoading(true);
                setError(null);

                // Buscando os co-produtores do produto
                const response = await api.get(`/api/product-participants/company/${companyId}/sent-invitations`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.success) {
                    const allInvitations = response.data.data || [];

                    // Filtrar apenas os convites do produto atual, se houver um productId
                    if (productIdToUse) {
                        const filteredInvitations = allInvitations.filter(
                            (invitation: Coproducer) => invitation.productId === productIdToUse
                        );
                        setSentInvitations(filteredInvitations);
                    } else {
                        setSentInvitations(allInvitations);
                    }
                } else {
                    setSentInvitations([]);
                }
            } catch (err: any) {
                console.error("Erro ao buscar convites:", err);
                setError(err.message || "Erro ao carregar convites enviados");
            } finally {
                setLoading(false);
            }
        };

        if (companyId) {
            fetchPendingInvitation();
        } else {
            console.log("CompanyId não encontrado. Não é possível buscar convites.");
        }
    }, [companyId, productIdToUse]);

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Cálculo para paginação dos convites
    const indexOfLastInvitation = currentPage * itemsPerPage;
    const indexOfFirstInvitation = indexOfLastInvitation - itemsPerPage;
    const currentInvitations = sentInvitations.slice(indexOfFirstInvitation, indexOfLastInvitation);
    const totalInvitationPages = Math.ceil(sentInvitations.length / itemsPerPage);

    // Cálculo para paginação dos co-produtores
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = coproducers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(coproducers.length / itemsPerPage);


    // Função para excluir um co-produtor (sem implementação de API por enquanto)
    const handleDeleteCoproducer = async () => {
        if (!coproducerToDelete) return;

        try {
            // Buscar a co-produção selecionada
            const coproduction = coproducers.find(cop => cop.id === coproducerToDelete);

            if (!coproduction) {
                throw new Error("Co-produção não encontrada");
            }

            setDeleteLoading(true);

            // Chamada para a API para revogar a co-produção
            const response = await api.post(
                `/api/product-participants/revoke/${coproduction.id}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Verificar resposta da API
            if (response.data && response.data.success) {
                // Atualizar a lista de co-produções, removendo a co-produção cancelada
                const updatedCoproductions = coproducers.filter(
                    cop => cop.id !== coproducerToDelete
                );
                setCoproducers(updatedCoproductions);

                // Mensagem de sucesso
                setAlertInfo({
                    show: true,
                    type: "success",
                    title: "Sucesso!",
                    message: "Co-produção cancelada com sucesso."
                });
            } else {
                throw new Error(response.data.error || "Erro ao cancelar co-produção");
            }
        } catch (err: any) {
            console.error("Erro ao cancelar co-produção:", err);

            // Mensagem de erro
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: err.message || "Ocorreu um erro ao cancelar a co-produção."
            });
        } finally {
            // Fechando o modal após execução
            setDeleteLoading(false);
            setDeleteModalOpen(false);
            setCoproducerToDelete(null);

            // Esconder a mensagem após 3 segundos
            setTimeout(() => setAlertInfo(null), 3000);
        }
    };

    const openDeleteModal = (coproducerId: string) => {
        setCoproducerToDelete(coproducerId);
        setDeleteModalOpen(true);
    };

    // Função para renderizar a tabela de convites
    const renderInvitationsTable = () => (
        <div className="overflow-hidden rounded-xl">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Data
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Email
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Produto
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Comissão
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Expiração do convite
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Status
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8">
                                Ações
                            </TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {currentInvitations.map((sentInvitation) => (
                            <TableRow key={sentInvitation.id}>
                                <TableCell className="px-5 py-4 text-start">
                                    <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                        {new Date(sentInvitation.sentAt || sentInvitation.createdAt).toLocaleDateString()}
                                    </span>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                        {sentInvitation.email}
                                    </span>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    {sentInvitation.product?.name}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    {sentInvitation.percentage}%
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    {sentInvitation.expiresAt ? new Date(sentInvitation.expiresAt).toLocaleDateString() : "Eterno"}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                    <Badge
                                        size="sm"
                                        color={sentInvitation.status === "pending" ? "warning" : (sentInvitation.status === "accepted" ? "success" : "error")}
                                    >
                                        {sentInvitation.status === "pending" ? "Pendente" : (sentInvitation.status === "accepted" ? "Aceito" : "Rejeitado")}
                                    </Badge>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                    <div className="flex justify-end gap-2 relative">
                                        {sentInvitation.type !== "revocation" && (
                                            <MoreButton
                                                options={[
                                                    {
                                                        label: "Cancelar",
                                                        onClick: () => openDeleteModal(sentInvitation.id),
                                                        icon: <Trash2 className="size-4" />,
                                                        className: "text-red-600 dark:text-red-400"
                                                    }
                                                ]}
                                            />
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {totalInvitationPages > 1 && (
                    <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalInvitationPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    // Função para renderizar a tabela de co-produtores
    const renderCoproducersTable = () => (
        <div className="overflow-hidden rounded-xl">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Data de início
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Nome
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Comissão
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Duração
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                Status
                            </TableCell>
                            <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8">
                                Ações
                            </TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {currentItems.length > 0 ? (
                            currentItems.map((coproducer) => (
                                <TableRow key={coproducer.id}>
                                    <TableCell className="px-5 py-4 text-start">
                                        <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                                            {new Date(coproducer.contractStart || coproducer.createdAt).toLocaleDateString()}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                            {coproducer.company?.name}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {coproducer.percentage}%
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {coproducer.contractDuration ? `${coproducer.contractDuration} dias` : "Eterno"}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={
                                                coproducer.status === "active" ? "success" :
                                                    coproducer.status === "pending_revocation" ? "info" :
                                                        "error"
                                            }
                                        >
                                            {
                                                coproducer.status === "active" ? "Ativo" :
                                                    coproducer.status === "pending_revocation" ? "Cancelamento pendente" :
                                                        "Inativo"
                                            }
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                        <div className="flex justify-end gap-2 relative">
                                            <MoreButton
                                                options={[
                                                    {
                                                        label: "Cancelar",
                                                        onClick: () => openDeleteModal(coproducer.id),
                                                        icon: <Trash2 className="size-4" />,
                                                        className: "text-red-600 dark:text-red-400"
                                                    }
                                                ]}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-4">
                                    Nenhum co-produtor encontrado
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {totalPages > 1 && (
                    <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    // Renderização principal
    if (loading) {
        return <div className="p-4 text-center dark:text-gray-400">Carregando co-produtores...</div>;
    }

    if (error) {
        return <div className="p-4 text-center text-red-500">Erro: {error}</div>;
    }

    return (
        <div>
            {/* Exibir tabelas se houver dados */}
            {(sentInvitations.length > 0 || coproducers.length > 0) ? (
                <div className="space-y-8">
                    {/* Campo de busca e botão de convidar */}
                    <div className="col-span-12 space-y-6">
                        <div className="mb-6">
                            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                                <div className="relative w-full md:w-1/3 lg:w-1/4">
                                    <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                        <svg
                                            className="fill-gray-500 dark:fill-gray-400"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                                fill=""
                                            />
                                        </svg>
                                    </button>
                                    <input
                                        type="text"
                                        placeholder="Pesquisar co-produtor..."
                                        className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                                    />
                                </div>
                                <Button
                                    size="md"
                                    variant="primary"
                                    disabled={loading}
                                    onClick={() => navigate(`/products/${productIdToUse}/co-producer/new`)}
                                >
                                    Convidar Co-Produtor
                                </Button>
                            </div>
                        </div>
                    </div>
                    {sentInvitations.length > 0 && sentInvitations[0].type === "coproducer" && (
                        <ComponentCard title="Convites enviados de co-produção">
                            {renderInvitationsTable()}
                        </ComponentCard>
                    )}
                    {coproducers.length > 0 && (
                        <ComponentCard title="Co-Produção">
                            {renderCoproducersTable()}
                        </ComponentCard>
                    )}
                </div>
            ) : (
                <ComponentCard title="Co-Produção">
                    <div className="flex flex-col items-center justify-center py-8">
                        <div className="p-8 text-center text-gray-500">
                            Este produto ainda não possui co-produtores.
                        </div>
                        <Button
                            size="md"
                            variant="primary"
                            disabled={loading}
                            onClick={() => navigate(`/products/${productIdToUse}/co-producer/new`)}
                        >
                            Convidar Co-Produtor
                        </Button>
                    </div>
                </ComponentCard>
            )}

            {/* Alertas e Modal */}
            {alertInfo && alertInfo.show && (
                <div className="mt-4">
                    <Alert
                        variant={alertInfo.type}
                        title={alertInfo.title}
                        message={alertInfo.message}
                    />
                </div>
            )}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                size="sm"
                onConfirm={handleDeleteCoproducer}
                confirmText="Cancelar"
                cancelText="Voltar"
                confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                confirmLoading={deleteLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tem certeza que deseja cancelar este contrato de co-produção?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Não será possível reverter esta ação!
                    </p>
                </div>
            </Modal>
        </div>
    );
}